import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import getLoyaltyIcon, { getLoyaltyIconSize } from '../../helpers/loyalty.utils';
import { Box, Flex, LinearProgress } from '@mafc/trolly';
import { IAppState } from '@mafc/common';
import COLORS from 'theme/colors';

import FEATURES_CONFIG from 'config/features';

import {
  LoyaltyPointWrapper,
  LoyaltyPointHeader,
  LoyaltyPointAvailable,
  SwitchLayout,
  LoyaltyPointAvailableRow,
} from './loyalty.styles';
import useLoyaltyPoints from './hooks/useLoyaltyPoints.hooks';
import { initStorageManager } from 'utils/customStorage.util';
import Constants from 'config/constants';
import { useDispatch, useSelector } from 'react-redux';
import useOrderSummary from 'common/OrderSummary/hooks/useOrderSummary.hook';
import { toggleLoyaltySwitch } from './redux/LoyaltyPoints.actions';
import { getEarnedPoints } from 'pages/Cart/redux/actions';
import { useFeature } from '@mafc/appshell';
const storage = initStorageManager(() => localStorage);
const { loyaltyIcon } = FEATURES_CONFIG.settings;

interface Props {
  loading: boolean;
}

const Loyalty = ({ loading }: Props) => {
  const {
    appConfig: {
      currency,
      currencyCodeLocalized,
      countryConfiguration: {
        paymentMethods: { isWebPaymentArchitectureV2Enabled },
      },
    },
    loyalty: { isToggleSwitchedOn },
  } = useSelector((state: IAppState) => state);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    removeCashback,
    loyaltyPoints: lPts,
    redeemPoints,
    removeRedeemedPoints,
  } = useLoyaltyPoints();
  const {
    cartTotalAmount,
    totalPointsAmount,
    redeemed,
    redeemedAmount: { priceStringValue = '' } = { priceStringValue: '' },
  } = lPts;
  const { loyaltyVouchers } = useOrderSummary();
  const {
    currencyValue: { value: loyaltyPointsRedeemed = 0 } = {
      value: 0,
    },
  } = loyaltyVouchers && loyaltyVouchers.length ? loyaltyVouchers[0] : {};

  useEffect(() => {
    const hasBeenRedeemed = Boolean(storage.getItem(Constants.redeemedSharePoints));
    if (removeCashback && redeemed && !hasBeenRedeemed) {
      removeRedeemedPoints(Number(priceStringValue) === cartTotalAmount);
      storage.setItem(Constants.redeemedSharePoints, 'true');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redeemed, loyaltyPointsRedeemed]);

  const redeemAllClick = useCallback(
    (e: any) => {
      const loyaltyPoints = Number(totalPointsAmount);
      const isFullCartRedemption = loyaltyPoints >= cartTotalAmount;
      const pointsToRedeem = isFullCartRedemption ? cartTotalAmount : loyaltyPoints;
      if (e.target.checked) {
        redeemPoints(pointsToRedeem, isFullCartRedemption);
      } else {
        removeRedeemedPoints(isFullCartRedemption);
      }
    },
    [redeemPoints, removeRedeemedPoints, totalPointsAmount, cartTotalAmount]
  );

  useEffect(() => {
    dispatch(toggleLoyaltySwitch(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Icon = getLoyaltyIcon(loyaltyIcon);
  const iconSize = getLoyaltyIconSize(loyaltyIcon);

  return (
    <Box my={3}>
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <LoyaltyPointWrapper py={3}>
          <Flex sx={{ position: 'relative' }} alignItems="center">
            <Box sx={{ textAlign: 'center' }} mr={3}>
              <Icon testid={loyaltyIcon} size="CUSTOM" {...iconSize} />
            </Box>
            <Flex justifyContent="space-between" alignItems="center" flex="auto">
              <Box>
                <LoyaltyPointHeader>{t('shareDiscount')}</LoyaltyPointHeader>
                <LoyaltyPointAvailableRow>
                  {redeemed
                    ? isWebPaymentArchitectureV2Enabled
                      ? t('loyaltyPoint.toRedeem')
                      : t('loyaltyPoint.redeemed')
                    : t('spc.balanceAvailable')}
                  :
                  <LoyaltyPointAvailable>
                    {currencyCodeLocalized || currency}{' '}
                    <Box as="span" ml={1} color={COLORS.BLACK}>
                      {redeemed ? priceStringValue : parseFloat(totalPointsAmount).toFixed(2)}
                    </Box>
                  </LoyaltyPointAvailable>
                </LoyaltyPointAvailableRow>
              </Box>
              <Box>
                <SwitchLayout onChange={redeemAllClick} checked={isToggleSwitchedOn} />
              </Box>
            </Flex>
          </Flex>
        </LoyaltyPointWrapper>
        {loading && <LinearProgress position="bottom" />}
      </Box>
    </Box>
  );
};

export default Loyalty;
